import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

type DebtRow = {
  id: number;
  source?: 'purchase' | 'expense';
  reference: string;
  supplierId?: number;
  supplierName?: string;
  total: number;
  paidAmount: number;
  balance: number;
  paymentDueDate?: string | null;
  daysOverdue: number;
  isOverdue: boolean;
  status: string;
  type?: string | null;
  paymentMethod?: string | null;
  createdAt: string;
};

type PaymentRow = {
  id: number;
  purchaseId: number;
  purchaseReference?: string;
  supplierId?: number;
  supplierName?: string;
  amount: number;
  paymentDate: string;
  paymentMethod?: string | null;
  reference?: string | null;
  notes?: string | null;
  createdByUserName?: string | null;
  createdAt: string;
};

type SupplierAgg = {
  supplierId?: number;
  supplierName?: string;
  purchaseCount: number;
  totalBalance: number;
  overdueBalance: number;
  nextDueDate?: string | null;
};

const fmt = (n: number) => n.toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('es-AR'); } catch { return s; }
};

export const DebtRankingPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [items, setItems] = useState<DebtRow[]>([]);
  const [bySupplier, setBySupplier] = useState<SupplierAgg[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/purchases/debt-ranking`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data?.items || []);
      setBySupplier(res.data?.bySupplier || []);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    setPaymentsLoading(true);
    try {
      const res = await api.getPurchasePayments({ pageSize: 100 });
      setPayments(res.items || []);
    } finally {
      setPaymentsLoading(false);
    }
  };

  useEffect(() => { load(); loadPayments(); }, []);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(r =>
      (r.reference || '').toLowerCase().includes(q) ||
      (r.supplierName || '').toLowerCase().includes(q) ||
      (r.type || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const totals = useMemo(() => {
    const tot = items.reduce((acc, r) => acc + r.balance, 0);
    const overdue = items.filter(r => r.isOverdue).reduce((acc, r) => acc + r.balance, 0);
    const dueIn30 = items.filter(r => !r.isOverdue && r.paymentDueDate &&
      (new Date(r.paymentDueDate).getTime() - Date.now()) <= 30 * 86400000
    ).reduce((acc, r) => acc + r.balance, 0);
    return { tot, overdue, dueIn30 };
  }, [items]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Ranking de deudas a proveedores</Typography>
          <Typography variant="body2" color="text.secondary">
            Orden de pago sugerido: vencidas primero, luego por fecha de vencimiento y saldo.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => { load(); loadPayments(); }} disabled={loading || paymentsLoading}>Actualizar</Button>
      </Stack>

      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Total adeudado</Typography>
            <Typography variant="h5" fontWeight={700}>{fmt(totals.tot)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1, borderLeft: '4px solid #d32f2f' }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Vencido</Typography>
            <Typography variant="h5" fontWeight={700} color="error.main">{fmt(totals.overdue)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1, borderLeft: '4px solid #ed6c02' }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary">Vence en 30 días</Typography>
            <Typography variant="h5" fontWeight={700} color="warning.main">{fmt(totals.dueIn30)}</Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab label={`Por compra (${items.length})`} />
              <Tab label={`Por proveedor (${bySupplier.length})`} />
              <Tab label={`Pagos registrados (${payments.length})`} />
            </Tabs>
            {tab === 0 && (
              <TextField
                size="small"
                placeholder="Buscar referencia / proveedor / tipo..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ width: 320 }}
              />
            )}
          </Stack>

          {loading ? <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box> :
            tab === 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Referencia</TableCell>
                    <TableCell>Proveedor</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>Vencimiento</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Pagado</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center">Sin deudas pendientes.</TableCell></TableRow>
                  ) : filteredItems.map(r => (
                    <TableRow key={`${r.source || 'purchase'}-${r.id}`} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(r.source === 'expense' ? '/expenses?tab=deudas' : '/purchases')}>
                      <TableCell>{r.reference}</TableCell>
                      <TableCell>{r.supplierName || '—'}</TableCell>
                      <TableCell>{r.type || '—'}</TableCell>
                      <TableCell>{r.paymentMethod || '—'}</TableCell>
                      <TableCell>{fmtDate(r.paymentDueDate)}</TableCell>
                      <TableCell align="right">{fmt(r.total)}</TableCell>
                      <TableCell align="right">{fmt(r.paidAmount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.balance)}</TableCell>
                      <TableCell>
                        {r.isOverdue ? (
                          <Chip size="small" color="error" label={`Vencida ${r.daysOverdue}d`} />
                        ) : r.paymentDueDate ? (
                          <Chip size="small" color="default" label="Al día" />
                        ) : (
                          <Chip size="small" variant="outlined" label="Sin vto." />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : tab === 1 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Proveedor</TableCell>
                    <TableCell align="right"># Compras</TableCell>
                    <TableCell>Próx. vencimiento</TableCell>
                    <TableCell align="right">Vencido</TableCell>
                    <TableCell align="right">Saldo total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bySupplier.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">Sin deudas pendientes.</TableCell></TableRow>
                  ) : bySupplier.map((s, i) => (
                    <TableRow key={s.supplierId ?? `none-${i}`} hover>
                      <TableCell>{s.supplierName || '— Sin proveedor —'}</TableCell>
                      <TableCell align="right">{s.purchaseCount}</TableCell>
                      <TableCell>{fmtDate(s.nextDueDate)}</TableCell>
                      <TableCell align="right" sx={{ color: s.overdueBalance > 0 ? 'error.main' : 'inherit', fontWeight: s.overdueBalance > 0 ? 600 : 400 }}>
                        {fmt(s.overdueBalance)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(s.totalBalance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : paymentsLoading ? (
              <Box sx={{ textAlign: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Proveedor</TableCell>
                    <TableCell>Compra</TableCell>
                    <TableCell>Método</TableCell>
                    <TableCell>Referencia</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell>Registrado por</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center">Todavía no se registraron pagos.</TableCell></TableRow>
                  ) : payments.map(p => (
                    <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate('/purchases')}>
                      <TableCell>{fmtDate(p.paymentDate)}</TableCell>
                      <TableCell>{p.supplierName || '—'}</TableCell>
                      <TableCell>{p.purchaseReference || `#${p.purchaseId}`}</TableCell>
                      <TableCell>{p.paymentMethod || '—'}</TableCell>
                      <TableCell>{p.reference || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(p.amount)}</TableCell>
                      <TableCell>{p.createdByUserName || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          }
        </CardContent>
      </Card>
    </Box>
  );
};

export default DebtRankingPage;
